// apiErrors.js
//
// Shared error-extraction utility for reading backend failure messages out
// of a failed axios request. Pure functions only — no side effects, no
// imports from axios. See MLO-026 (Behavior §2) for the extraction rules
// this module implements.
//
// Public contract (cross-coder API surface — do not rename):
//   - NETWORK_ERROR_MESSAGE
//   - getApiErrorMessage(error)
//   - parseApiError(error)

export const NETWORK_ERROR_MESSAGE =
  "Network error. Please check your connection and try again.";

// Keys that carry a page-level (non-field) message rather than a
// field-keyed validation error. Excluded from `fieldErrorLists`.
const NON_FIELD_KEYS = ["non_field_errors", "detail", "error"];

function isText(value) {
  return typeof value === "string";
}

// A "text or list of texts" — the shape the validation ladder step accepts
// for any given entry. Anything else (numbers, objects, mixed arrays, ...)
// yields no texts and the entry is skipped.
function collectTexts(value) {
  if (isText(value)) {
    return [value];
  }
  if (Array.isArray(value) && value.every(isText)) {
    return value;
  }
  return [];
}

// Runs the Behavior §2 extraction ladder against an already-parsed response
// body. Returns the three pieces both public functions are built from:
//   - fieldErrorLists: { [fieldName]: string[] } — validation-shape entries
//     only, excluding non_field_errors/detail/error.
//   - nonFieldMessage: detail text, else error text, else non_field_errors
//     texts joined with ", ", else null.
//   - joined: the single extracted message per the strict ladder (detail →
//     error → validation-shape join of every usable entry, in server key
//     order), or null when nothing usable was found.
function extractFromData(data) {
  const empty = { fieldErrorLists: {}, nonFieldMessage: null, joined: null };

  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return empty;
  }

  // Step 1: a `detail` text wins outright.
  if (isText(data.detail)) {
    return { fieldErrorLists: {}, nonFieldMessage: data.detail, joined: data.detail };
  }

  // Step 2: otherwise an `error` text wins.
  if (isText(data.error)) {
    return { fieldErrorLists: {}, nonFieldMessage: data.error, joined: data.error };
  }

  // Step 3: validation shape — collect every usable entry, in the order the
  // server sent them; entries whose values are not texts or lists of texts
  // are skipped.
  const fieldErrorLists = {};
  const joinedParts = [];

  Object.keys(data).forEach((key) => {
    const texts = collectTexts(data[key]);
    if (texts.length === 0) {
      return;
    }
    joinedParts.push(...texts);
    if (!NON_FIELD_KEYS.includes(key)) {
      fieldErrorLists[key] = texts;
    }
  });

  const nonFieldTexts = collectTexts(data.non_field_errors);
  const nonFieldMessage = nonFieldTexts.length > 0 ? nonFieldTexts.join(", ") : null;
  const joined = joinedParts.length > 0 ? joinedParts.join(", ") : null;

  return { fieldErrorLists, nonFieldMessage, joined };
}

// Detects the two situations that sit outside the extraction ladder: no
// server response was ever received (network failure) vs. a response that
// simply carries nothing usable.
function isNetworkFailure(error) {
  return !error.response && Boolean(error.request);
}

/**
 * Turns a failed axios request into a single displayable string, or null
 * when nothing usable was found. Status code is never inspected — an
 * extracted message always wins regardless of status.
 *
 * @param {*} error - the caught axios error (or any error-shaped object).
 * @returns {string | null}
 */
export function getApiErrorMessage(error) {
  if (!error) {
    return null;
  }

  if (!error.response) {
    return isNetworkFailure(error) ? NETWORK_ERROR_MESSAGE : null;
  }

  return extractFromData(error.response.data).joined;
}

/**
 * Structured variant of `getApiErrorMessage` for surfaces that need
 * field-level display alongside a page-level message (e.g. the rules form).
 *
 * @param {*} error - the caught axios error (or any error-shaped object).
 * @returns {{
 *   isNetworkError: boolean,
 *   message: string | null,
 *   fieldErrorLists: Object<string, string[]>,
 *   nonFieldMessage: string | null,
 * }}
 */
export function parseApiError(error) {
  const empty = { isNetworkError: false, message: null, fieldErrorLists: {}, nonFieldMessage: null };

  if (!error) {
    return empty;
  }

  if (!error.response) {
    if (isNetworkFailure(error)) {
      return { ...empty, isNetworkError: true, message: NETWORK_ERROR_MESSAGE };
    }
    return empty;
  }

  const { fieldErrorLists, nonFieldMessage, joined } = extractFromData(error.response.data);
  return { isNetworkError: false, message: joined, fieldErrorLists, nonFieldMessage };
}
